import { MutationTree, ActionTree, ActionContext, GetterTree } from "vuex";
import { RootState } from "../index";
import { Locales } from "@/models/locales";
import { User, UserStatusType } from "@/models/user";
import {AuthRequests, configService} from "@/helpers/index";
import * as firebase from "firebase/app";
import "firebase/auth";
import axios from "axios";

export interface SessionState {
  user: User | null;
  tmpUser: Object | undefined;
  locales: Locales[];
  status: UserStatusType;
  token: string | null;
  authMode: AuthModeType | undefined;
  isLoading: boolean;
  authType: string | null;
  error: string | null;
  notice: string;
}

export type AuthModeType = "login" | "signup";
type SessionContext = ActionContext<SessionState, RootState>;

export const namespaced = true;

export const state = (): SessionState => ({
  user: null,
  tmpUser: undefined,
  locales: [],
  status: "notLogged",
  token: null,
  authMode: "login",
  isLoading: false,
  authType: null,
  error: null,
  notice: ""
});

export const getters: GetterTree<SessionState, RootState> = {};

export const mutations: MutationTree<SessionState> = {
  startLogging(state: SessionState) {
    state.status = "logging";
  },
  setAuthMode(state: SessionState, data: { mode: AuthModeType }) {
    state.authMode = data.mode;
  },
  setAuthType(state: SessionState, authType: any) {
    state.authType = authType;
  },
  setTmpUser(state: SessionState, tmpUser: Object) {
    state.tmpUser = tmpUser;
  },
  clearTmpUser(state: SessionState) {
    state.tmpUser = undefined;
  },
  setCurrentUser(state: SessionState, auth: { user: User; token: string }) {
    state.user = auth.user;
    state.token = auth.token;
    state.status = "logged";
  },
  updateCurrentUserData(state: SessionState, user: User) {
    state.user = user;
  },
  setLocales(state: SessionState, locales: Locales[]): void {
    state.locales = locales;
  },
  cleanCurrentUser(state: SessionState) {
    state.status = "notLogged";
    state.token = null;
    state.user = null;
  },
  setLoading(state: SessionState, loading: boolean) {
    state.isLoading = loading;
  },
  setNewToken(state: SessionState, data: { token: string }) {
    state.token = data.token;
  }
};

export const actions: ActionTree<SessionState, RootState> = {
  setLoading(context: SessionContext, loading: boolean) {
    context.commit("setLoading", loading);
  },
  updateCurrentUserdata(context: SessionContext, user: User) {
    context.commit("updateCurrentUserData", user);
  },
  manualLogin(
    context: SessionContext,
    data: { email: string; password: string }
  ) {
    context.commit("startLogging");
    context.commit("setLoading", true);
    context.commit("setAuthMode", { mode: "login" });
    return AuthRequests.signIn(data.email, data.password).then(() => {
      context.dispatch("autoAuth");
      context.commit("setLoading", false);
    });
  },
  // logout(context: SessionContext): Promise<void> {
  //   return AuthRequests.signOut()
  //     .then(() => {
  //       context.commit("cleanCurrentUser");
  //     })
  //     .catch((error: any) => console.log(error));
  // },
  logout(context: SessionContext) {
    AuthRequests.signOut();
    context.commit("cleanCurrentUser");
  },
  setLocales(context) {
    context.commit("setLanguages");
  },
  manualSignup(
    context: SessionContext,
    data: { email: string; password: string; name: string; surname: string }
  ) {
    context.commit("startLogging");
    context.commit("setLoading", true);
    context.commit("setAuthMode", { mode: "signup" });
    context.commit("setTmpUser", {
      email: data.email,
      first_name: data.name,
      last_name: data.surname
    });
    return AuthRequests.signUp(data.email, data.password, data.name, data.surname, data.password).then(() => {
      context.dispatch("autoAuth");
      context.commit("setLoading", false);
    }).catch(err => {
      throw new Error(err.message);
    });
  },
  socialAccess(context: SessionContext, { token, user, provider }) {
    context.commit("startLogging");
    context.commit("setLoading", true);
    context.commit("setAuthMode", { mode: "login" });
    return AuthRequests.signInSocial(provider, token, user).then(() => {
      context.dispatch("autoAuth");
      context.commit("setLoading", false);
    });
  },
  autoAuth(context: SessionContext): Promise<any> {
    context.commit("startLogging");
    //const currentUser = firebase.auth().currentUser;
    const currentUser = AuthRequests.autoAuthUser();
    if (currentUser == null) {
      return Promise.reject(
        new Error(
          "SessionModule.actions.autoLogin: current user cannot be null."
        )
      );
    } else {
      if (!currentUser.token) {
        throw Error("Missing token");
      }
          // @ts-ignore
          return Promise.all([
            AuthRequests.fetchUserDetails(
              currentUser.token,
              context.state.tmpUser,
              context.state.authMode
            ),
            currentUser.token
          ])
        .then((response: [any, string]) => {
          context.commit("clearTmpUser");
          context.commit("setAuthMode", { mode: "" });
          context.commit("setCurrentUser", {
            user: response[0],
            token: response[1]
          });
          context.commit("setAuthType", response[0].type);
        })
        .catch(error => {
          context.commit("clearTmpUser");
          context.commit("setAuthMode", { mode: "" });
          context.dispatch("logout");
          throw error;
        });
    }
  },
  resetPassword(context: SessionContext, options: any): any {
    if (options.type === "resetPassword") {
      return AuthRequests.resetPassword(options).then((success: boolean) => {
        context.commit("setLoading", false);
        return success;
      });
    }
    if (options.type === "changePassword") {
      return AuthRequests.changePassword(options).then((success: boolean) => {
        return success;
      });
    }
  }
};
